import React, {useEffect, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import API from "../api/config.js"; // Ensure your API configuration is correct
import WLogo from "../assets/QRscriptsWLogo.png";
import DLogo from "../assets/QRscriptsLogo.png";
import {BsBag} from "react-icons/bs";

// Helper to get initial theme (as in your original code)
const getInitialTheme = () => {
    if (typeof window !== "undefined") {
        return localStorage.getItem("travelxoxo-theme") || "light";
    }
    return "light";
};

// Reusable Input component (updated to use modern Tailwind classes)
const Input = ({label, id, type = "text", value, onChange, required = true, placeholder, className = ""}) => (
    <div className="space-y-2">
        <label
            htmlFor={id}
            className="block text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
        >
            {label} {required && <span className="text-orange-500">*</span>}
        </label>
        <input
            id={id}
            type={type}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className={`w-full p-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-500 ${className}`}
        />
    </div>
);

// Reusable Select component for QR Code Type
const Select = ({label, id, value, onChange, required = true, options}) => (
    <div className="space-y-2">
        <label
            htmlFor={id}
            className="block text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
        >
            {label} {required && <span className="text-orange-500">*</span>}
        </label>
        <select
            id={id}
            value={value ?? ""}
            onChange={onChange}
            required={required}
            className="w-full p-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white appearance-none pr-10"
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
);

export default function RequestForm() {
    // Original props, though qrId, navigate, location are less relevant for a new request form

    const navigate = useNavigate();
    const location = useLocation();
    const verifiedEmail = location.state?.email;

    // Theme state + effect
    const [theme, setTheme] = useState(getInitialTheme);
    useEffect(() => {
        if (theme === "dark") {
            document.documentElement.classList.add("dark");
            document.body.classList.add("bg-gray-900", "text-white");
            document.body.classList.remove("bg-gray-50", "text-gray-900");
        } else {
            document.documentElement.classList.remove("dark");
            document.body.classList.remove("bg-gray-900", "text-white");
            document.body.classList.add("bg-gray-50", "text-gray-900");
        }
        localStorage.setItem("travelxoxo-theme", theme);
    }, [theme]);
    const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

    // 📝 NEW FORM STATE for QR Code Request
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: verifiedEmail || "",
        address: "",
        qrCodeQuantity: 1, // Default quantity
        qrCodeType: "Sticker", // Default type
    });

    // State for UI feedback
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [ok, setOk] = useState(false);

    // 📝 Form change handler
    const onChange = (e) => {
        const {id, value} = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [id]: value,
        }));
        // Clear status on change
        if (error || ok) {
            setError(null);
            setOk(false);
        }
    };

    // 📝 Form submission handler

    const onSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        if (!form.name || !form.phone || !form.email || !form.address || !form.qrCodeQuantity || !form.qrCodeType) {
            setError("Please fill in all required fields.");
            return;
        }

        const quantity = parseInt(form.qrCodeQuantity, 10);
        if (isNaN(quantity) || quantity <= 0) {
            setError("QR Code Quantity must be a positive number.");
            return;
        }

        setSaving(true);
        setError(null);
        setOk(false);

        try {
            const response = await API.post("/requests", {
                name: form.name,
                phone: form.phone,
                email: form.email,
                address: form.address,
                qr_quantity: quantity,
                qr_type: form.qrCodeType,
            });

            if (response.status === 201 || response.status === 200) {
                setOk(true);

                // reset form
                setForm({
                    name: "",
                    phone: "",
                    email: "",
                    address: "",
                    qrCodeQuantity: 1,
                    qrCodeType: "sticker",
                });

                // 🔁 Redirect after 1.5 seconds
                setTimeout(() => {
                    navigate("/"); // change path if needed
                }, 1500);
            } else {
                setError(response.data?.message || "An unexpected error occurred during submission.");
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Failed to submit request. Please try again.";
            setError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto p-6 min-h-screen dark:bg-gray-900 transition-colors duration-300">
            <div className="flex justify-between items-center mb-8">
                <img
                    src={theme === "dark" ? DLogo : WLogo}
                    alt="TXO Logo"
                    className="h-10 w-auto object-contain transition-all duration-300"
                />

                <div className="flex flex-col items-center gap-1">
                    <button
                        className="w-12 h-12 rounded-full bg-pink-600 hover:bg-pink-700 text-white flex items-center justify-center transition-transform duration-200 hover:scale-105 shadow-lg"
                        onClick={() => window.open("https://travelxoxo.io", "_blank")}
                        title="Shop Now"
                    >
                        <BsBag className="text-white text-xl" />
                    </button>

                    <span className="text-[10px] font-semibold uppercase tracking-wide text-white">Shop Now</span>
                </div>
            </div>

            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6">Request QR Codes 📦</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
                Please fill out the form below to request new QR code tags. We will reach out to you soon!
            </p>

            {/* Status Messages */}
            {error && (
                <div className="mb-4 p-4 rounded-xl border border-red-300 bg-red-50 text-red-700 dark:bg-red-900 dark:border-red-700 dark:text-red-300 font-medium">
                    ⚠️ {error}
                </div>
            )}
            {ok && (
                <div className="mb-4 p-4 rounded-xl border border-green-300 bg-green-50 text-green-700 dark:bg-green-900 dark:border-green-700 dark:text-green-300 font-medium">
                    ✅ Request submitted successfully!
                </div>
            )}

            {/* 📝 Request Form */}
            <form className="space-y-6" onSubmit={onSubmit}>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 pt-4">Contact Information</h2>
                <div className="space-y-4">
                    <Input label="Name" id="name" value={form.name} onChange={onChange} placeholder="John Doe" />
                    <Input
                        label="Phone Number"
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="(123) 456-7890"
                    />
                    <Input
                        label="Email"
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="john.doe@example.com"
                    />
                    <Input
                        label="Shipping Address"
                        id="address"
                        value={form.address}
                        onChange={onChange}
                        placeholder="123 Main St, City, Zip"
                    />
                </div>

                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 pt-4">Order Details</h2>
                <div className="space-y-4">
                    <Input
                        label="QR Code Quantity"
                        id="qrCodeQuantity"
                        type="number"
                        value={form.qrCodeQuantity}
                        onChange={onChange}
                        placeholder="e.g., 5"
                        min="1"
                    />

                    <Select
                        label="QR Code Type"
                        id="qrCodeType"
                        value={form.qrCodeType}
                        onChange={onChange}
                        options={[
                            {value: "Sticker", label: "Sticker (Recommended for phones/laptops)"},
                            {value: "Tag", label: "Plastic Card (Recommended for keys/wallets)"},
                        ]}
                    />
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    className="w-full py-4 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg dark:bg-orange-500 dark:hover:bg-pink-600 hover:bg-pink-700"
                >
                    {saving ? "Submitting Request..." : "Submit QR Code Request"}
                </button>
            </form>
        </div>
    );
}
